import * as yup from 'yup';

// Known plants in the system
export const KNOWN_PLANTS = [
  'Ambatale WTP',
  'Kandy WTP',
  'Galle WTP',
  'Matara WTP',
  'Colombo North WTP',
  'Other Plant'
];

export const adminSchema = yup.object().shape({
  // Name Validation - Letters only (and spaces/hyphens)
  first_name: yup
    .string()
    .required('First name is required')
    .matches(
      /^[a-zA-Z\s'.-]+$/,
      'First name must contain only letters, spaces, dots, hyphens, or apostrophes'
    )
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must not exceed 50 characters')
    .trim(),
  
  last_name: yup
    .string()
    .required('Last name is required')
    .matches(
      /^[a-zA-Z\s'.-]+$/,
      'Last name must contain only letters, spaces, dots, hyphens, or apostrophes'
    )
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must not exceed 50 characters')
    .trim(),

  // Email Validation - Valid email format
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .lowercase()
    .trim(),

  // Username Validation
  username: yup
    .string()
    .required('Username is required')
    .matches(
      /^[a-zA-Z0-9._-]+$/,
      'Username can only contain letters, numbers, dots, underscores, and hyphens'
    )
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must not exceed 30 characters')
    .lowercase()
    .trim(),

  // Password Validation
  // Requirements: Minimum 8 characters, at least one uppercase, one lowercase, one number, one special character
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])/,
      'Password must contain at least one lowercase letter'
    )
    .matches(
      /^(?=.*[A-Z])/,
      'Password must contain at least one uppercase letter'
    )
    .matches(
      /^(?=.*\d)/,
      'Password must contain at least one number'
    )
    .matches(
      /^(?=.*[@$!%*?&])/,
      'Password must contain at least one special character (@$!%*?&)'
    ),

  // Confirm Password
  confirm_password: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password'), null], 'Passwords must match'),

  // Role Selection
  role: yup
    .string()
    .required('Role is required')
    .oneOf(
      ['Administrator', 'Shift Manager', 'AGM'],
      'Invalid role selected'
    ),

  // Plant Validation - Must be from known plants
  plant: yup
    .string()
    .required('Plant is required')
    .oneOf(
      KNOWN_PLANTS,
      'Please select a valid plant from the list'
    ),
});

// Password strength meter validation (optional, for UI feedback)
export const validatePasswordStrength = (password) => {
  let strength = 0;
  const feedback = [];

  if (!password) {
    return { strength: 0, feedback: ['Password is required'] };
  }

  // Length
  if (password.length >= 8) strength += 1;
  if (password.length >= 12) strength += 1;

  // Contains lowercase
  if (/[a-z]/.test(password)) {
    strength += 1;
  } else {
    feedback.push('Add lowercase letters');
  }

  // Contains uppercase
  if (/[A-Z]/.test(password)) {
    strength += 1;
  } else {
    feedback.push('Add uppercase letters');
  }

  // Contains numbers
  if (/\d/.test(password)) {
    strength += 1;
  } else {
    feedback.push('Add numbers');
  }

  // Contains special characters
  if (/[@$!%*?&]/.test(password)) {
    strength += 1;
  } else {
    feedback.push('Add special characters (@$!%*?&)');
  }

  return {
    strength: Math.min(strength, 5), // Max strength of 5
    feedback,
    isStrong: strength >= 5,
  };
};

// Validate unique username (this would typically call an API)
export const validateUsernameUniqueness = async (username) => {
  // TODO: Replace with actual API call
  // return await api.post('/validate/username', { username });
  return { isUnique: true };
};

// Validate unique email (this would typically call an API)
export const validateEmailUniqueness = async (email) => {
  // TODO: Replace with actual API call
  // return await api.post('/validate/email', { email });
  return { isUnique: true };
};
